import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { ENVS, forgetEnv, resolveEnv, type Env } from './env';

/**
 * One client per environment, and every one of them can hold a session.
 *
 * WHAT CHANGED, 2026-08-22, AND WHY
 * ---------------------------------
 * Until today there was exactly one auth-capable client — `supabaseProto` — and
 * a second, `supabaseLive`, built with `persistSession: false` for data reads
 * only. auth.tsx, MfaGate.tsx and Security.tsx all imported the Proto client
 * directly, so `useEnv()` switched only which project a PAGE read. Identity did
 * not move.
 *
 * That was survivable while the second env was switched off. It stops being
 * survivable the moment Stage is in the switch, because a JWT minted by one
 * Supabase project is not a credential at another: sessions, users and MFA
 * factors are per-project. Concretely, MFA enrolment on a Stage route would
 * have written the factor into Proto's `auth.mfa_factors` while Stage's 23
 * restrictive policies went on checking a Stage claim that could never exist —
 * an admin that looks enrolled and is not.
 *
 * So the clients are now symmetric. Each carries its own `storageKey`, which is
 * what lets both sessions live side by side: signing in to Stage does not evict
 * the Proto session, and switching tabs restores whichever session that env
 * already has rather than bouncing the operator to the login form.
 *
 * Proto keeps the ORIGINAL storage key. Renaming it would have silently signed
 * out every existing admin on deploy, and auth.tsx reads that exact key to
 * decide its bootstrap budget.
 */

interface EnvWiring {
  url: string | undefined;
  key: string | undefined;
  /** localStorage key holding this env's session. Must be unique per env. */
  storageKey: string;
}

// 2026-09-28 — CS-5: Stage only. The Proto wiring (VITE_SUPABASE_URL_PROTO / _ANON_KEY_PROTO,
// session key 'votd_admin_session') is retired with the admin's Proto side; see env.tsx.
const WIRING: Record<Env, EnvWiring> = {
  stage: {
    url:        import.meta.env.VITE_SUPABASE_URL_STAGE as string | undefined,
    key:        import.meta.env.VITE_SUPABASE_ANON_KEY_STAGE as string | undefined,
    storageKey: 'votd_admin_session_stage',
  },
  // Dev-stage (2026-10-27): its own URL/key pair and its own saved session, so signing in here never
  // touches the Prod session. Missing vars → no client → the tab is greyed out, never mis-pointed.
  devstage: {
    url:        import.meta.env.VITE_SUPABASE_URL_DEVSTAGE as string | undefined,
    key:        import.meta.env.VITE_SUPABASE_ANON_KEY_DEVSTAGE as string | undefined,
    storageKey: 'votd_admin_session_devstage',
  },
};

/** The build settings that wire `env`, for messages that tell an operator what is missing. */
export function envVarSuffix(env: Env): string {
  return env === 'devstage' ? 'DEVSTAGE' : 'STAGE';
}

export function sessionStorageKey(env: Env): string {
  return WIRING[env].storageKey;
}

/**
 * `lock` is overridden with a no-op because Supabase's default uses
 * navigator.locks to coordinate token refresh across tabs. Navigator.locks
 * is shared across all tabs/windows of the same origin and can ORPHAN —
 * closed tab mid-auth, bfcache restoration, multi-tab race — leaving the
 * lock "held" by a ghost that never releases. Subsequent login attempts
 * then wait on that lock indefinitely (manifests as the Login button
 * stuck on "Signing in..." in normal browser; works in incognito because
 * fresh storage = no orphaned lock).
 *
 * The trade-off: with the no-op lock, two tabs racing on token refresh
 * could overwrite each other (one tab gets logged out, has to re-login).
 * For a single-editor admin tool that's a much smaller cost than the
 * perennial hang the navigator.locks orphan was producing.
 *
 * Diagnosis trail: 6f25185 (2026-04-27) added a 10s bootstrap timeout to
 * route AROUND the orphan; this lock override addresses the root cause.
 */
function build(env: Env): SupabaseClient | null {
  const w = WIRING[env];
  if (!w.url || !w.key) return null;
  return createClient(w.url, w.key, {
    auth: {
      persistSession:     true,
      storageKey:         w.storageKey,
      autoRefreshToken:   true,
      detectSessionInUrl: false,
      lock: async (_name, _acquireTimeout, fn) => fn(),
    },
  });
}

const CLIENTS: Record<Env, SupabaseClient | null> = {
  stage: build('stage'),
  devstage: build('devstage'),
};

export function getClient(env: Env): SupabaseClient | null {
  return CLIENTS[env];
}

/** True when THIS env has both a URL and a key. Ask per env — never globally. */
export function isConfigured(env: Env): boolean {
  return CLIENTS[env] !== null;
}

/** Envs that actually have a client. Used to hide a tab that would only 404. */
export function configuredEnvs(): Env[] {
  return ENVS.filter(isConfigured);
}

/**
 * The env to AUTHENTICATE against — never an env this build cannot reach.
 *
 * THE LOCKOUT THIS PREVENTS (found 2026-08-22, before it reached anyone)
 * --------------------------------------------------------------------
 * `resolveEnv` falls back to the remembered choice when the path carries no env,
 * which is exactly the case on `/login` and `/auth/callback`. Combine that with a
 * build where one env has a URL but no anon key — precisely the state of every
 * build until the Stage key is added to Cloudflare Pages — and you get a trap:
 *
 *   1. operator opens /stage/... directly (bookmark, pasted link, a redirect)
 *   2. EnvProvider stores "stage" as the remembered env
 *   3. no Stage client exists, so they are bounced to /login
 *   4. /login has no env prefix, so it resolves to the REMEMBERED env: stage
 *   5. getClient('stage') is null -> "Authentication service not configured."
 *   6. reload does not help. The bad choice is in localStorage, and there is no
 *      env-prefixed route on the login page to override it.
 *
 * A dead end that survives a refresh, on the one screen with no way to change
 * env. So the auth path never trusts the remembered value on its own: an
 * unreachable choice is discarded, forgotten so it cannot trap the next load,
 * and replaced with an env that actually has a client.
 */
export function resolveAuthEnv(pathname: string, search = ''): Env {
  const want = resolveEnv(pathname, search);
  if (isConfigured(want)) return want;
  forgetEnv();
  return configuredEnvs()[0] ?? want;
}

/**
 * The admin's doors: every read and write the admin makes goes through a function in the
 * `api_admin` schema (CS-5, migrations 20260928162259 + 20260928172920). Schema `public` is
 * closed to signed-in callers since CS-4, so a `.from('<table>')` on the default schema fails.
 * Each door checks for an active admin at aal2 (whoami answers at aal1, for sign-in).
 *
 *   reads:  adminDb(env).rpc('sourced_queue').select(...).eq(...)   — filters apply to the result
 *   writes: adminDb(env).rpc('sourced_queue_update', { p_id, p_fields })  — returns the id, or errors
 */
// Typed loosely on purpose: the admin reads these rows untyped, exactly as it did with `.from()` on
// the untyped client before CS-5. With a typed schema, `.rpc('x').select(...)` cannot know whether a
// function returns one row or a set and types every result as `Row | Row[]`, breaking every page.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function adminDb(env: Env): any {
  const c = getClient(env);
  return c ? c.schema('api_admin') : null;
}

// ── Project identity ──────────────────────────────────────────────────────────
//
// Which Supabase PROJECT a page is actually reading is not a constant — it is
// whatever the build's env vars point at. Pages must resolve it at runtime and
// SAY it, never assume it.
//
// Why this exists (2026-08-21): PipelineStatus.tsx imported `supabaseProto`
// directly and its header claimed "Data is Proto-only", so it read the Proto
// project no matter which env the operator was in. After an environment
// rotation that page would have shown a healthy Proto while the project the
// admin was actually configured against burned. A monitoring surface that does
// not name its own database is not a monitoring surface.
//
// The ref is parsed from the configured URL — it cannot drift from reality. The
// name map is a human convenience only; an unmapped ref is reported loudly as
// UNRECOGNIZED rather than being quietly prettified into something reassuring.
//
// SAC Pilot stays in this map after being dropped from `Env` on purpose: if a
// build is ever mis-wired to point an env at it, the banner must say "SAC Pilot"
// rather than "UNRECOGNIZED PROJECT". A name map is for recognising, not for
// listing what is currently allowed.
//
// Refs: docs/rules/environments.md ("the topology in one screen").
const PROJECT_NAMES: Record<string, string> = {
  xtxznjnqikuabjyotjbi: 'Proto',
  aqbuwpncrurkloczbvkg: 'Prod',   // was Stage until the 2026-10-16 launch
  mmhqfjryxbwhzlrtqmcs: 'Dev-stage', // test data only; deployed admin votd-admin-devstage (2026-10-26)
  dunktqzfprbjqxbcnooi: 'SAC Pilot',
};

export interface ProjectInfo {
  env: Env;
  /** Full configured URL, or null when the env var is missing. */
  url: string | null;
  /** Project ref parsed out of the URL (the `xxxx` in xxxx.supabase.co). */
  ref: string | null;
  /** Human name for the ref, or an explicit failure string. Never guessed. */
  name: string;
  /** True only when `ref` is one this app has a name for. */
  known: boolean;
  /** True when a Supabase client actually exists for this env (URL + key). */
  configured: boolean;
}

function refFromUrl(url: string | undefined): string | null {
  if (!url) return null;
  const m = /^https?:\/\/([a-z0-9-]+)\.supabase\./i.exec(url.trim());
  return m ? m[1] : null;
}

/**
 * The colour a project is shown in, from its ref — never from the env key or the hostname:
 * red = Prod (real members), green = dev-stage (test data), amber = anything else (founder 2026-10-26).
 */
export type Tone = 'prod' | 'devstage' | 'unknown';
export function toneOf(ref: string | null): Tone {
  if (ref === 'aqbuwpncrurkloczbvkg') return 'prod';
  if (ref === 'mmhqfjryxbwhzlrtqmcs') return 'devstage';
  return 'unknown';
}

/** Resolve, at runtime, which Supabase project `env` is wired to. */
export function getProjectInfo(env: Env): ProjectInfo {
  const url = WIRING[env].url;
  const ref = refFromUrl(url);
  const name = ref
    ? (PROJECT_NAMES[ref] ?? 'UNRECOGNIZED PROJECT')
    : 'NOT CONFIGURED';
  return {
    env,
    url: url || null,
    ref,
    name,
    known: Boolean(ref) && ref! in PROJECT_NAMES,
    configured: isConfigured(env),
  };
}
